/**
 * Jyotish Tarang — Privacy Policy (public page, no auth required)
 * Added 13 Aug 2026: /privacy previously had no route at all, so it fell
 * through App.tsx's catch-all ("*" -> /dashboard), which is wrapped in
 * <Protected>, which bounced unauthenticated visitors to /login. Google
 * Play Console requires a live, publicly reachable privacy policy URL,
 * which surfaced this. Content below reflects the app's actual data
 * practices as documented in mobile/PLAY_STORE_LISTING.md's "Data safety
 * form" prep notes and verified against backend/api/auth.py (account
 * deletion) and cron_cleanup_activity.py (180-day post-deletion activity
 * log retention) rather than generic boilerplate.
 */
import React from 'react';

const wrap: React.CSSProperties = {
  maxWidth: 760, margin: '0 auto', padding: '48px 24px 96px',
  fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
  color: '#2b2b2b', lineHeight: 1.65,
};
const h1: React.CSSProperties = { fontSize: 28, marginBottom: 4 };
const updated: React.CSSProperties = { color: '#777', fontSize: 14, marginBottom: 32 };
const h2: React.CSSProperties = { fontSize: 19, marginTop: 36, marginBottom: 10 };
const table: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', marginTop: 12, fontSize: 14 };
const th: React.CSSProperties = { textAlign: 'left', borderBottom: '2px solid #ddd', padding: '8px 10px' };
const td: React.CSSProperties = { borderBottom: '1px solid #eee', padding: '8px 10px', verticalAlign: 'top' };
const backLink: React.CSSProperties = { display: 'inline-block', marginBottom: 24, color: '#5b3fa0', textDecoration: 'none', fontSize: 14 };

export default function PrivacyPage() {
  return (
    <div style={wrap}>
      <a href="/" style={backLink}>&larr; Jyotish Tarang</a>
      <h1 style={h1}>Privacy Policy</h1>
      <div style={updated}>Last updated: 13 August 2026</div>

      <p>
        Jyotish Tarang (published on Google Play as <strong>Jyotishacharya: Vedic Astro</strong>)
        is a Vedic astrology app. This policy explains what information we collect, why, and how
        you can control or delete it. If anything here is unclear, email{' '}
        <a href="mailto:support@jyotish.vivekkhare.org">support@jyotish.vivekkhare.org</a>.
      </p>

      <h2 style={h2}>Information we collect</h2>
      <table style={table}>
        <thead>
          <tr>
            <th style={th}>Data</th>
            <th style={th}>Why</th>
            <th style={th}>Shared with a third party?</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={td}>Email address</td>
            <td style={td}>Account creation and sign-in (email OTP)</td>
            <td style={td}>No</td>
          </tr>
          <tr>
            <td style={td}>Name</td>
            <td style={td}>Personalizing your charts and reports</td>
            <td style={td}>No</td>
          </tr>
          <tr>
            <td style={td}>Phone number (optional)</td>
            <td style={td}>WhatsApp reminders, if you opt in</td>
            <td style={td}>No</td>
          </tr>
          <tr>
            <td style={td}>Birth date, time and place</td>
            <td style={td}>Required to calculate your chart — this is the core function of the app</td>
            <td style={td}>No — processed on our servers only</td>
          </tr>
          <tr>
            <td style={td}>Gender (optional)</td>
            <td style={td}>Chart personalization</td>
            <td style={td}>No</td>
          </tr>
          <tr>
            <td style={td}>Questions you ask "Ask AI"</td>
            <td style={td}>Generating an answer to your question</td>
            <td style={td}><strong>Yes — Anthropic</strong>, solely to process and answer your question. Never used for advertising.</td>
          </tr>
        </tbody>
      </table>

      <p style={{ marginTop: 16 }}>
        We do <strong>not</strong> sell your data, and we do not use it for advertising. All data
        is encrypted in transit (HTTPS). We do not request or collect your device's precise
        location. Payment details are handled directly by our payment processor (Razorpay) — we
        never see or store your card or bank details.
      </p>

      <h2 style={h2}>How long we keep it</h2>
      <p>
        Your account data is kept for as long as your account is active. If you delete your
        account (see below), we immediately hard-delete your charts, AI conversation history,
        and notification preferences, and anonymize your name and email on our records. Payment
        and subscription records are retained separately as required for financial and tax
        recordkeeping. A record of account activity (not its content — just that an action
        happened, when, and its outcome) is retained for up to 180 days after deletion so we can
        respond to legitimate compliance or government audit requests, then permanently purged.
      </p>

      <h2 style={h2}>Deleting your account</h2>
      <p>
        You can delete your account any time from the app: <strong>Profile → Danger zone →
        Delete my account</strong>, then confirm twice. If you can't access the app, email{' '}
        <a href="mailto:support@jyotish.vivekkhare.org">support@jyotish.vivekkhare.org</a> from
        your registered address and we'll do it for you. See{' '}
        <a href="/delete-account">jyotish.vivekkhare.org/delete-account</a> for full details.
      </p>

      <h2 style={h2}>Your rights</h2>
      <p>
        You can access, correct, or export your chart data from within the app at any time, and
        request deletion as described above. To exercise any of these rights, or ask us anything
        about this policy, email{' '}
        <a href="mailto:support@jyotish.vivekkhare.org">support@jyotish.vivekkhare.org</a>.
      </p>

      <h2 style={h2}>Children's privacy</h2>
      <p>
        Jyotish Tarang is not directed at children under 13, and we do not knowingly collect data
        from them. Charts for children are expected to be created by a parent or guardian using
        their own account.
      </p>

      <h2 style={h2}>Changes to this policy</h2>
      <p>
        If this policy changes, we'll update the "Last updated" date above. Continued use of the
        app after a change means you accept the updated policy.
      </p>

      <h2 style={h2}>Contact us</h2>
      <p>
        Jyotish Tarang / Jyotishacharya: Vedic Astro<br />
        Email: <a href="mailto:support@jyotish.vivekkhare.org">support@jyotish.vivekkhare.org</a>
      </p>
    </div>
  );
}
